import { AuthService } from '@services/auth.service';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import {
  FormArray,
  FormControl,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { User } from '@classes/user/user';
import { Role } from '@classes/role';
import { UiService } from '@services/ui.service';
import { UsersService } from '@services/users.service';
import { UserDetail } from '@classes/user/user-detail';
import { Client } from '@classes/client';
import { RoleName } from '@enums/role-name.enum';
import { RolesService } from '@services/role.service';
import { SitesService } from '@services/sites.service';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { Feature } from '@classes/feature';
import { FeaturesService } from '@services/features.service';
import { debug } from 'console';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() user: User;

  rolesList: Role[];
  clientsList: Client[] = [];

  selectableClients: Client[] = [];

  sitesList: Site[] = [];
  selectableSites: Site[] = [];
  selRole: Role;
  userNamePattern = '^[a-z0-9._-]{3,50}$';
  canChooseClient: boolean;
  canChooseSite: boolean;
  canChooseFeatures: boolean = false;
  canHaveFeatures: boolean = true;

  stringMaxLength = 50;

  allFeatures: Feature[] = [];
  selectablesFeatures: Feature[] = [];
  featuresFormArray: FormArray;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private rolesSrv: RolesService,
    private usersSrv: UsersService,
    private clientsSrv: ClientsService,
    private sitesSrv: SitesService,
    private authSrv: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private featuresSrv: FeaturesService,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.canChooseClient =
      this.authSrv.getUser().role.name === RoleName.bizzdev ||
      this.authSrv.getUser().role.name === RoleName.super_admin;
    this.canChooseFeatures =
      this.authSrv.getUser().role.name === RoleName.bizzdev ||
      this.authSrv.getUser().role.name === RoleName.super_admin ||
      this.authSrv.getUser().role.name === RoleName.admin;

    if (this.user) {
      this.canHaveFeatures =
        this.user.role.name !== RoleName.bizzdev &&
        this.user.role.name !== RoleName.super_admin;
    }

    this.initSubscriptions();
  }

  get isClientRequired(): boolean {
    return this.selRole && this.selRole.name !== RoleName.admin;
  }

  get isSiteRequired(): boolean {
    // return this.selRole && this.selRole.name !== RoleName.admin;
    return true;
  }

  get canAddClient(): boolean {
    const restrictedRoles = [
      RoleName.admin,
      RoleName.client,
      RoleName.admin_client,
      RoleName.driver,
      RoleName.dispatcher,
    ];
    return (
      !this.selRole || !restrictedRoles.some((r) => r === this.selRole.name)
    );
  }

  get canAddSite(): boolean {
    const restrictedRoles = [];
    return (
      !this.selRole || !restrictedRoles.some((r) => r === this.selRole.name)
    );
  }

  onAddClient(client?: Client): void {
    if (!client) {
      this.generateSelectableClients();
    }

    const control = this.fb.control(client || null);
    // if(this.isClientRequired) {
    control.setValidators(Validators.required);
    // }
    this.getArray('clients').push(control);

    this.changeDetectorRef.detectChanges();
  }

  onAddSite(site?: Site): void {
    if (!site) {
      this.generateSelectableSites();
    }
    const control = this.fb.control(site || null);
    if (this.isSiteRequired) {
      control.setValidators(Validators.required);
    }
    this.getArray('sites').push(control);

    this.changeDetectorRef.detectChanges();
  }

  onRemoveClient(index: number): void {
    this.getArray('clients').removeAt(index);

    this.generateSelectableClients();
  }

  onRemoveSite(index: number): void {
    this.getArray('sites').removeAt(index);
    this.generateSelectableSites();
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.user) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  private update(): void {
    const userCopy: User = JSON.parse(JSON.stringify(this.user));
    userCopy.username = this.editForm.value.username;
    userCopy.email = this.editForm.value.email;
    userCopy.role = this.selRole;
    if (userCopy.detail == null) {
      userCopy.detail = new UserDetail();
    }

    userCopy.sites = this.editForm.value.sites;
    userCopy.allSites = this.editForm.value.allSites;
    userCopy.detail.firstname = this.editForm.value.detail.firstname;
    userCopy.detail.lastname = this.editForm.value.detail.lastname;
    userCopy.detail.phone = this.editForm.value.detail.phone;

    userCopy.clients = this.editForm.value.clients.filter((c) => c != null);

    let selectedFeaturesId = this.editForm.value.features
      .map((checked, i) => (checked ? this.selectablesFeatures[i].id : null))
      .filter((v) => v !== null);

    this.usersSrv.put(userCopy).subscribe((userResponse) => {
      if (
        selectedFeaturesId.length > 0 &&
        !this.editForm.get('features').pristine &&
        this.canHaveFeatures
      ) {
        if (this.selRole.name === RoleName.driver) {
          selectedFeaturesId.push(
            this.allFeatures.find((f) => f.name === 'Véhicule').id,
          );
        }

        this.featuresSrv
          .putUserFeatures(userResponse.id, selectedFeaturesId)
          .subscribe((user) => {
            this.usersSrv.updateUsersListSubject(user);
            this.isLoading = false;
          });
      } else if (
        selectedFeaturesId.length === 0 &&
        !this.editForm.get('features').pristine &&
        this.canHaveFeatures
      ) {
        this.featuresSrv
          .putUserFeatures(userResponse.id, selectedFeaturesId)
          .subscribe((user) => {
            this.usersSrv.updateUsersListSubject(user);
            this.isLoading = false;
          });
      } else {
        this.isLoading = false;
      }
    });
  }

  private create(): void {
    this.user = new User(
      this.editForm.value.username,
      this.editForm.value.email,
      this.selRole,
      this.editForm.value.clients.filter((c) => c != null),
      this.editForm.value.sites.filter((s) => s != null),
      this.editForm.value.allSites,
      this.editForm.value.detail.firstname,
      this.editForm.value.detail.lastname,
      this.editForm.value.detail.phone,
    );

    const selectedFeaturesId = this.editForm.value.features
      .map((checked, i) => (checked ? this.selectablesFeatures[i].id : null))
      .filter((v) => v !== null);

    if (
      this.selRole.name === RoleName.driver &&
      selectedFeaturesId.length > 0
    ) {
      selectedFeaturesId.push(
        this.allFeatures.find((f) => f.name === 'Véhicule').id,
      );
    }

    this.usersSrv.post(this.user).subscribe(
      (userResponse) => {
        if (selectedFeaturesId.length > 0 && this.canHaveFeatures) {
          this.featuresSrv
            .putUserFeatures(userResponse.id, selectedFeaturesId)
            .subscribe((user) => {
              this.usersSrv.updateUsersListSubject(user);
              this.isLoading = false;
            });
        } else {
          this.isLoading = false;
        }
      },
      () => (this.user = null),
    );
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const rolesPromise = new Promise((resolve, reject) => {
      this.rolesSrv.getList().subscribe((rolesFromSrv) => {
        this.rolesList = rolesFromSrv;
        resolve(rolesFromSrv);
      });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList().subscribe((clientsFromSrv) => {
        this.clientsList = clientsFromSrv;
        resolve(clientsFromSrv);
      });
    });

    const featuresPromise = new Promise((resolve, reject) => {
      if (this.user) {
        this.featuresSrv
          .getByClient(this.user.clients[0].id)
          .subscribe((featuresFromSrv) => {
            this.allFeatures = featuresFromSrv;
            resolve(featuresFromSrv);
          });
      } else {
        if (this.canChooseClient) {
          resolve('ok');
        } else {
          this.featuresSrv.get().subscribe((featuresFromSrv) => {
            this.allFeatures = featuresFromSrv;
            resolve(featuresFromSrv);
          });
        }
      }
    });

    Promise.all([rolesPromise, clientsPromise, featuresPromise]).then(() => {
      this.initForm();
      this.generateSelectableClients();
      this.isLoading = false;
    });
  }

  private initForm(): void {
    let username = '';
    let email = '';
    let role = '';
    let firstname = '';
    let lastname = '';
    let phone = '';
    let clients: Client[] = [];
    let sites: Site[] = [];
    let allSites: boolean = false;
    let features = [];

    if (this.user) {
      username = this.user.username;
      email = this.user.email;
      role = this.user.role.id;
      clients = this.user.clients;
      sites = this.user.sites;
      allSites = this.user.allSites;
      this.selRole = this.user.role;
      features = this.user.features;

      if (this.user.detail) {
        firstname = this.user.detail.firstname;
        lastname = this.user.detail.lastname;
        phone = this.user.detail.phone;
      }
    } else {
      // si l'utilisateur n'est pas superadmin/bizzdev alors il doit rajouter des users dans son client
      if (!this.canChooseClient) {
        clients[0] = this.clientsList[0];
      }
    }

    this.editForm = this.fb.group({
      username: [
        username,
        [
          Validators.required,
          Validators.pattern(this.userNamePattern),
          Validators.maxLength(this.stringMaxLength),
        ],
      ],
      email: [
        email,
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(this.stringMaxLength),
        ],
      ],
      role: [role, Validators.required],
      clients: this.fb.array([]),
      sites: this.fb.array([]),
      allSites: [allSites],
      detail: this.fb.group({
        firstname: [firstname, Validators.maxLength(this.stringMaxLength)],
        lastname: [lastname, Validators.maxLength(this.stringMaxLength)],
        phone: [phone, Validators.maxLength(this.stringMaxLength)],
      }),
      features: this.fb.array([]),
    });

    this.canChooseSite = false;

    this.getArray('clients').valueChanges.subscribe((results) => {
      this.handleClientsChange(results);
    });

    if (clients && clients.length) {
      clients.map((c) => this.onAddClient(c));
    } else {
      this.onAddClient();
    }

    if (this.user && this.user.clients) {
      let clientIdsUser = [];
      this.user.clients.map((oneClient) => {
        clientIdsUser.push(oneClient.id);
      });
      this.sitesSrv
        .getListByClients(clientIdsUser)
        .subscribe((sitesFromSrv) => {
          this.sitesList = sitesFromSrv;
          this.generateSelectableSites();
          this.changeDetectorRef.detectChanges();
          this.canChooseSite = true;
        });
    }

    if (sites && sites.length > 0) {
      sites.map((s) => this.onAddSite(s));
    } else if (!this.user?.allSites) {
      this.onAddSite();
    }

    this.observeFormChanges();
    this.addFeatures();

    if (features?.length > 0) {
      this.selectablesFeatures.map((feature, index) => {
        if (features.find((f) => f.id === feature.id)) {
          let formArrayFeatures = this.editForm.get('features') as FormArray;
          formArrayFeatures.controls[index].setValue(true);
        }
      });
    }
  }

  private observeFormChanges(): void {
    this.editForm.controls.role.valueChanges.subscribe((selId: string) => {
      this.selRole = this.rolesList.find((r) => r.id === selId);
      let saveSite = this.getArray('sites').value;
      if (this.selRole.name === 'driver') {
        this.editForm.controls.allSites.setValue(true);
      } else if (
        this.selRole.name === 'client' ||
        this.selRole.name === 'admin_client'
      ) {
        this.editForm.controls.allSites.setValue(false);
      }

      this.canHaveFeatures =
        this.selRole.name !== 'bizzdev' && this.selRole.name !== 'super_admin';
      this.addFeatures();

      // reset sites
      this.getArray('sites').clear();
      saveSite.map((s) => this.onAddSite(s));
    });

    this.editForm.controls.allSites.valueChanges.subscribe(
      (allSites: boolean) => {
        if (allSites) {
          this.editForm.controls.sites.clearValidators();
          this.getArray('sites').clear();
        } else {
          this.getArray('sites').clear();
          this.onAddSite();
        }

        this.generateSelectableSites();
      },
    );
  }

  private handleClientsChange(results: Client[]): void {
    console.log('handleClientsChange', results);
    const clientIds = [];
    let canRequest = true;

    if (results && results.length > 0 && results[0] !== null) {
      results.map((oneClient) => {
        if (oneClient && oneClient.id) {
          clientIds.push(oneClient.id);
        } else {
          canRequest = false;
        }
      });

      if (clientIds[0] && canRequest) {
        this.sitesList = [];
        if (this.getArray('sites')) {
          this.getArray('sites').value.map((siteControl, index) => {
            this.onRemoveSite(index);
          });
        }

        this.sitesSrv.getListByClients(clientIds).subscribe((sitesFromSrv) => {
          this.sitesList = sitesFromSrv;
          this.generateSelectableSites();
          this.changeDetectorRef.detectChanges();
          this.canChooseSite = true;
        });

        this.featuresSrv
          .getByClient(clientIds[0])
          .subscribe((featuresFromSrv) => {
            this.allFeatures = featuresFromSrv;
            this.addFeatures();
          });
      }
    } else {
      this.canChooseSite = false;
    }
  }

  addFeatures(): void {
    this.featuresFormArray = this.editForm.get('features') as FormArray;

    this.featuresFormArray.clear();

    this.selectablesFeatures = [...this.allFeatures];

    if (this.selRole && this.selRole.name === RoleName.driver) {
      this.selectablesFeatures = this.selectablesFeatures.filter(
        (feature) => feature.name !== 'Véhicule',
      );
    }

    this.selectablesFeatures.forEach(() =>
      this.featuresFormArray.push(new FormControl(false)),
    );
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  generateSelectableClients(): void {
    this.selectableClients = [...this.clientsList].filter(
      (worksite) =>
        !this.alreadyUsedClients.map((item) => item.id).includes(worksite.id),
    );
  }

  get clientsFormArray(): FormArray {
    return this.editForm.get('clients') as FormArray;
  }

  get alreadyUsedClients(): Client[] {
    return this.clientsFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }

  generateSelectableSites(): void {
    this.selectableSites = [...this.sitesList].filter(
      (worksite) =>
        !this.alreadyUsedSites.map((item) => item.id).includes(worksite.id),
    );
  }

  get sitesFormArray(): FormArray {
    return this.editForm.get('sites') as FormArray;
  }

  get alreadyUsedSites(): Site[] {
    return this.sitesFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }
}
